import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ANALYTICS_CONSENT,
  acceptAnalyticsConsent,
  initializeAnalytics,
  readAnalyticsConsent,
  rejectAnalyticsConsent,
} from "../lib/analytics";

export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(() => readAnalyticsConsent());
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const copy = t("cookieConsent", { returnObjects: true });

  useEffect(() => {
    const syncConsent = () => setConsent(readAnalyticsConsent());
    window.addEventListener("storage", syncConsent);

    return () => window.removeEventListener("storage", syncConsent);
  }, []);

  useEffect(() => {
    if (consent === ANALYTICS_CONSENT.ACCEPTED) {
      initializeAnalytics();
    }
  }, [consent]);

  const visible = consent === ANALYTICS_CONSENT.UNDECIDED;

  const accept = () => {
    setConsent(ANALYTICS_CONSENT.ACCEPTED);
    acceptAnalyticsConsent();
  };

  const reject = () => {
    setConsent(ANALYTICS_CONSENT.REJECTED);
    rejectAnalyticsConsent();
  };

  const savePreferences = () => {
    if (analyticsEnabled) accept();
    else reject();
  };

  return visible ? (
    <Banner
      role="dialog"
      aria-label={copy.ariaLabel}
      initial={{ opacity: 0, y: 18, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.26, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <AccentLine aria-hidden="true" />
      <HeaderRow>
        <Eyebrow>{copy.eyebrow}</Eyebrow>
        <StatusDot aria-hidden="true" />
      </HeaderRow>
      <Title>{copy.title}</Title>
      <Body>{copy.body}</Body>

      {showPreferences ? (
        <PreferencesPanel>
          <PreferenceRow>
            <PreferenceCopy>
              <PreferenceTitle>{copy.necessary.title}</PreferenceTitle>
              <PreferenceText>{copy.necessary.body}</PreferenceText>
            </PreferenceCopy>
            <FixedLabel>{copy.necessary.status}</FixedLabel>
          </PreferenceRow>
          <PreferenceRow>
            <PreferenceCopy>
              <PreferenceTitle>{copy.analytics.title}</PreferenceTitle>
              <PreferenceText>{copy.analytics.body}</PreferenceText>
            </PreferenceCopy>
            <ToggleLabel>
              <ToggleInput
                type="checkbox"
                checked={analyticsEnabled}
                onChange={(event) => setAnalyticsEnabled(event.target.checked)}
              />
              <ToggleVisual aria-hidden="true" />
            </ToggleLabel>
          </PreferenceRow>
        </PreferencesPanel>
      ) : null}

      <Actions>
        <PrimaryButton type="button" onClick={accept}>
          {copy.actions.accept}
        </PrimaryButton>
        <SecondaryButton type="button" onClick={reject}>
          {copy.actions.reject}
        </SecondaryButton>
        {showPreferences ? (
          <TextButton type="button" onClick={savePreferences}>
            {copy.actions.save}
          </TextButton>
        ) : (
          <TextButton type="button" onClick={() => setShowPreferences(true)}>
            {copy.actions.preferences}
          </TextButton>
        )}
      </Actions>
    </Banner>
  ) : null;
}

const Banner = styled(motion.aside)`
  position: fixed;
  right: clamp(16px, 2.8vw, 32px);
  bottom: clamp(16px, 2.8vw, 32px);
  z-index: 45;
  width: min(420px, calc(100vw - 32px));
  overflow: hidden;
  border: 1px solid rgba(190, 231, 241, 0.18);
  border-radius: 24px;
  background:
    radial-gradient(circle at 92% 0%, rgba(126, 202, 221, 0.16), transparent 38%),
    linear-gradient(145deg, rgba(18, 22, 24, 0.92), rgba(5, 7, 8, 0.88));
  box-shadow:
    0 24px 70px rgba(0, 0, 0, 0.44),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(18px);
  padding: 22px;
  color: #fff;

  @media (max-width: 540px) {
    right: 12px;
    bottom: 12px;
    width: calc(100vw - 24px);
    border-radius: 20px;
    padding: 18px;
  }
`;

const AccentLine = styled.span`
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: linear-gradient(180deg, rgba(190, 231, 241, 0.92), rgba(126, 202, 221, 0.12));
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const Eyebrow = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(190, 231, 241, 0.8);
`;

const StatusDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: rgba(190, 231, 241, 0.95);
  box-shadow: 0 0 18px rgba(126, 202, 221, 0.72);
`;

const Title = styled.h2`
  margin: 0;
  max-width: 19rem;
  font-size: 21px;
  line-height: 1.12;
  letter-spacing: -0.02em;
`;

const Body = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.62;
  color: rgba(255, 255, 255, 0.74);
`;

const PreferencesPanel = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 16px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.035);
`;

const PreferenceRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
`;

const PreferenceCopy = styled.div`
  display: grid;
  gap: 3px;
`;

const PreferenceTitle = styled.span`
  font-size: 13px;
  font-weight: 800;
`;

const PreferenceText = styled.span`
  font-size: 12px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.62);
`;

const FixedLabel = styled.span`
  min-width: 58px;
  text-align: center;
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.74);
  font-size: 12px;
  font-weight: 800;
`;

const ToggleLabel = styled.label`
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 48px;
  height: 28px;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const ToggleVisual = styled.span`
  width: 48px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  transition: background 0.2s ease, border-color 0.2s ease;

  &:after {
    content: "";
    position: absolute;
    top: 5px;
    left: 5px;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.9);
    transition: transform 0.2s ease, background 0.2s ease;
  }

  ${ToggleInput}:checked + & {
    border-color: rgba(190, 231, 241, 0.62);
    background: rgba(126, 202, 221, 0.18);
  }

  ${ToggleInput}:checked + &:after {
    transform: translateX(20px);
    background: #bee7f1;
  }
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 10px;
  margin-top: 18px;
  align-items: center;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const PrimaryButton = styled.button`
  min-height: 44px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: #050505;
  font-size: 13px;
  font-weight: 850;
  cursor: pointer;
`;

const SecondaryButton = styled.button`
  min-height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.055);
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
`;

const TextButton = styled.button`
  min-height: 44px;
  border: 0;
  background: transparent;
  color: rgba(190, 231, 241, 0.9);
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  padding: 0 4px;

  @media (max-width: 420px) {
    justify-self: center;
  }
`;
